import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PPEData } from '../types/ppe';

interface SearchPageProps {
  data: PPEData;
}

const SearchPage: React.FC<SearchPageProps> = ({ data }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedRiskLevel, setSelectedRiskLevel] = useState('');

  // Get all certificates from all categories
  const allCertificates = useMemo(() => {
    return data.categories.flatMap(category => 
      category.certificates.map(cert => ({
        ...cert,
        categoryName: category.name,
        categoryId: category.id
      }))
    );
  }, [data]);

  // Filter certificates based on search criteria
  const filteredCertificates = useMemo(() => {
    return allCertificates.filter(cert => {
      const matchesSearch = searchTerm === '' || 
        cert.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = selectedCategory === '' || 
        cert.categoryId === selectedCategory;

      const matchesRiskLevel = selectedRiskLevel === '' || 
        cert.riskLevel === selectedRiskLevel;

      return matchesSearch && matchesCategory && matchesRiskLevel;
    });
  }, [allCertificates, searchTerm, selectedCategory, selectedRiskLevel]);



  const getRiskLevelName = (level: string) => {
    return data.riskLevels[level]?.name || `Category ${level}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b-2 border-brand-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-black mb-4">Search PPE Certificates</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find the right protective equipment by searching our comprehensive database of EU PPE certificates.
            </p>
          </div>
        </div>
      </div>

      {/* Search Form */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search Term */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-black mb-2">
                Search Term
              </label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="e.g., EN388, cut level 5"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-yellow focus:outline-none transition-colors duration-200"
              />
            </div>

            {/* Category Filter */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-black mb-2">
                Category
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-yellow focus:outline-none transition-colors duration-200"
              >
                <option value="">All Categories</option>
                {data.categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Risk Level Filter */}
            <div>
              <label htmlFor="riskLevel" className="block text-sm font-medium text-black mb-2">
                Risk Level
              </label>
              <select
                id="riskLevel"
                value={selectedRiskLevel}
                onChange={(e) => setSelectedRiskLevel(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-brand-yellow focus:outline-none transition-colors duration-200"
              >
                <option value="">All Risk Levels</option>
                {Object.entries(data.riskLevels).map(([level, info]) => (
                  <option key={level} value={level}>
                    {info.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Filters */}
          {(searchTerm || selectedCategory || selectedRiskLevel) && (
            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                  setSelectedRiskLevel('');
                }}
                className="text-brand-yellow hover:text-primary-600 transition-colors duration-200 font-medium"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Results Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-black mb-2">
            {filteredCertificates.length} Certificate{filteredCertificates.length !== 1 ? 's' : ''} Found
          </h2>
          <p className="text-gray-600">
            {searchTerm || selectedCategory || selectedRiskLevel 
              ? 'Showing filtered results' 
              : 'Showing all available certificates'
            }
          </p>
        </div>

        {/* Results Grid */}
        {filteredCertificates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCertificates.map((cert) => (
              <Link
                key={cert.id}
                to={`/certificate/${cert.id}`}
                className="card hover:scale-105 transition-transform duration-200 group"
              >
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-base font-medium bg-[#1B559C] text-white">
                      {cert.code}
                    </div>
                    
                  </div>
                  <h3 className="text-xl font-bold text-black mb-2 group-hover:text-brand-yellow transition-colors duration-200">
                    {cert.name}
                  </h3>
                  <p className="text-gray-600 mb-3">{cert.description}</p>
                  <div className="text-sm text-gray-500 bg-gray-50 px-2 py-1 rounded">
                    {cert.categoryName}
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    Risk Level {cert.riskLevel}: {getRiskLevelName(cert.riskLevel)}
                  </span>
                  <div className="text-brand-yellow font-medium group-hover:text-primary-600 transition-colors duration-200">
                    View Details →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          /* No Results */
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-black mb-2">No Certificates Found</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Try adjusting your search criteria or browse all categories to find what you're looking for.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                  setSelectedRiskLevel('');
                }}
                className="btn-primary"
              >
                Clear Filters
              </button>
              <Link to="/" className="btn-secondary">
                Browse Categories
              </Link>
            </div>
          </div>
        )}

        {/* Search Tips */}
        <div className="mt-12 card max-w-4xl mx-auto">
          <h3 className="text-lg font-bold text-black mb-4">Search Tips</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-black mb-2">Search by Certificate Code</h4>
              <p className="text-sm text-gray-600">
                Try searching for specific standards like "EN388" for cut-resistant gloves or "EN166" for eye protection.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-black mb-2">Filter by Category</h4>
              <p className="text-sm text-gray-600">
                Use the category filter to focus on specific types of protection like head, hand, or foot protection.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-black mb-2">Risk Level Filtering</h4>
              <p className="text-sm text-gray-600">
                Filter by risk level to find equipment suitable for your specific workplace hazards.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-black mb-2">Need Help?</h4>
              <p className="text-sm text-gray-600">
                Visit our main shop for expert advice on choosing the right PPE for your needs.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Powered by Section */}
      <div className="bg-black text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-3">
            <span className="text-gray-400">PPE Certificate Portal powered by</span>
            <img 
              src="/sws-logo.png" 
              alt="Safety Workwear Shop" 
              className="h-8 w-auto opacity-80"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
